import React, { useState } from 'react';
import { motion } from 'framer-motion';
import GlobalFilters from '../components/GlobalFilters';
import FilterTabs from '../components/FilterTabs';
import MediaDisplaySection from '../components/MediaDisplaySection';
import { useFilteredData } from '../hooks/useFilteredData';

const Dashboard = () => {
  const [performanceFilter, setPerformanceFilter] = useState('all');
  const { filteredData, statistics, hasActiveFilters, loading, error } = useFilteredData();

  // Apply performance filter locally on Dashboard
  const filteredByPerformance = filteredData.filter(media => {
    if (performanceFilter === 'all') return true;

    // Calculate metrics for filtering
    const channels = Object.values(media.channels);
    const overallScore = Math.round(
      channels.reduce((sum, channel) => sum + channel.score, 0) / channels.length
    );
    const avgRoas = channels.reduce((sum, channel) => sum + channel.roas, 0) / channels.length;
    const avgCpa = channels.reduce((sum, channel) => sum + channel.cpa, 0) / channels.length;

    switch (performanceFilter) {
      case 'high-performance':
        return overallScore > 80;
      case 'high-roas':
        return avgRoas > 5;
      case 'low-cpa':
        return avgCpa < 200;
      case 'needs-improvement':
        return overallScore < 60;
      default:
        return true;
    }
  });

  // Calculate statistics for performance-filtered data
  const performanceFilteredStats = {
    totalMedia: filteredByPerformance.length,
    totalRevenue: filteredByPerformance.reduce((sum, media) => {
      return sum + Object.values(media.channels).reduce((channelSum, channel) => 
        channelSum + channel.revenue, 0
      );
    }, 0),
    totalSpent: filteredByPerformance.reduce((sum, media) => {
      return sum + Object.values(media.channels).reduce((channelSum, channel) => 
        channelSum + channel.spent, 0
      );
    }, 0),
    totalConversions: filteredByPerformance.reduce((sum, media) => {
      return sum + Object.values(media.channels).reduce((channelSum, channel) => 
        channelSum + channel.conversions, 0
      );
    }, 0),
    averageRoas: 0,
    averageScore: 0
  };

  // Calculate averages
  if (performanceFilteredStats.totalSpent > 0) {
    performanceFilteredStats.averageRoas = parseFloat(
      (performanceFilteredStats.totalRevenue / performanceFilteredStats.totalSpent).toFixed(1)
    );
  }

  if (filteredByPerformance.length > 0) {
    performanceFilteredStats.averageScore = Math.round(
      filteredByPerformance.reduce((sum, media) => {
        const channels = Object.values(media.channels);
        const overallScore = Math.round(
          channels.reduce((sum, channel) => sum + channel.score, 0) / channels.length
        );
        return sum + overallScore;
      }, 0) / filteredByPerformance.length
    );
  }

  const hasPerformanceFilter = performanceFilter !== 'all';
  const totalActiveFilters = hasActiveFilters || hasPerformanceFilter;

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold text-gray-900">แดชบอร์ดภาพรวม</h1>
          <p className="text-gray-600 mt-2">ติดตามและวิเคราะห์ประสิทธิภาพโฆษณาแบบเรียลไทม์</p>
        </motion.div>

        {/* Global Filters - Show performance filter on dashboard */}
        <GlobalFilters 
          showPerformanceFilter={false}
          showChannelFilter={true}
          showSearchFilter={true}
          showDateFilter={true}
        />

        {/* Performance Filter Tabs - Dashboard specific */}
        <FilterTabs 
          activeFilter={performanceFilter} 
          onFilterChange={setPerformanceFilter} 
        />

        {/* Stats Overview */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-5 gap-6 my-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="text-2xl font-bold text-gray-900">{performanceFilteredStats.totalMedia}</div>
            <div className="text-sm text-gray-500">สื่อโฆษณา</div>
            {totalActiveFilters && (
              <div className="text-xs text-blue-600 mt-1">
                จาก {filteredData.length} รายการ
              </div>
            )}
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="text-2xl font-bold text-green-600">฿{performanceFilteredStats.totalRevenue.toLocaleString()}</div>
            <div className="text-sm text-gray-500">รายได้รวม</div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="text-2xl font-bold text-blue-600">฿{performanceFilteredStats.totalSpent.toLocaleString()}</div>
            <div className="text-sm text-gray-500">งบประมาณที่ใช้</div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="text-2xl font-bold text-purple-600">{performanceFilteredStats.averageRoas}x</div>
            <div className="text-sm text-gray-500">ROAS เฉลี่ย</div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="text-2xl font-bold text-orange-600">{performanceFilteredStats.averageScore}</div>
            <div className="text-sm text-gray-500">คะแนนเฉลี่ย</div>
          </div>
        </motion.div>

        {/* Results Info */}
        {totalActiveFilters && (
          <motion.div
            className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-between">
              <div className="text-blue-800">
                <span className="font-medium">ผลการกรอง:</span> แสดง {performanceFilteredStats.totalMedia} จาก {filteredData.length} รายการ
                {hasPerformanceFilter && (
                  <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                    ตามประสิทธิภาพ
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Media Display Section */}
        <MediaDisplaySection
          mediaData={filteredByPerformance}
          totalOriginalCount={filteredData.length}
          hasActiveFilters={totalActiveFilters}
          loading={loading}
          error={error}
        />
      </main>
    </div>
  );
};

export default Dashboard;
